import React, { useState, useEffect } from 'react'
import InputFields from '../components/InputFields';
import { useNavigate } from "react-router-dom"
import { useSignIn } from '../hooks/hooks'
import { showErrorToast, showSuccessToast } from '../components/common';
import { resendEmailVerification } from '../API/auth';
import { useTranslation } from "react-i18next";
import './../i18n';
import { useDispatch } from 'react-redux';
import { fetchSettings } from '../hooks/settingsSlice';
import { fetchSidebarMenu } from '../API/settings';

const LogIn = () => {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        try {
            const accessToken = localStorage.getItem('AccessToken');
            const currentUser = JSON.parse(localStorage.getItem('CurrentUser') || '{}');
            
            if (accessToken && currentUser) {
                if (currentUser.isOnboarding === true) {
                    navigate('/dashboard');
                } else {
                    navigate('/onboarding');
                }
            }
        } catch (error) {
            console.error('Error checking login status:', error);
        }
    }, [navigate]);

    const [inputValue, setInputValue] = useState({ email: '', password: '', isAdmin: true });
    const [Error, setError] = useState({ email: '', password: '' });
    const [showEmailVerificationMessage, setShowEmailVerificationMessage] = useState(false);
    const [isResendingVerification, setIsResendingVerification] = useState(false);

    const { mutate: loginUser } = useSignIn();
    const [isLoading, setIsLoading] = useState(false);
  

    const logInUser = (inputValue) => {
        loginUser(inputValue, {
            onSuccess: (res) => {
                if (res.status === 200 && res?.data?.token) {
                    const currentUser = res?.data?.user;
                    localStorage.setItem("CurrentUser", JSON.stringify(currentUser));
                    localStorage.setItem("AccessToken", JSON.stringify(res?.data?.token));
                    dispatch(fetchSettings(currentUser?.catererDetails?._id));
                    
                    // Fetch sidebar menu and store in Redux
                    fetchSidebarMenu();

                    if(currentUser.preferredLanguage){
                        i18n.changeLanguage(currentUser.preferredLanguage);
                        // Also save to localStorage to ensure persistence
                        localStorage.setItem('CurrentUser', JSON.stringify(currentUser));
                    }

                    if (!currentUser?.isOnboarding) {
                        navigate('/onboarding');
                        return;
                    }

                    navigate('/');
                    return;
                }
                
                // Check for email verification requirement
                if (res?.data?.isEmailVerified === false) {
                    setShowEmailVerificationMessage(true);
                    setIsLoading(false);
                    return;
                }
                
                if (!res?.success) {
                    showErrorToast("Invalid username and password");
                    return;
                }
                setIsLoading(false);
            },
            onError: (res) => {
                setIsLoading(false);
                if (res?.response?.status === 400) {
                    showErrorToast(res?.response?.data?.message || "Invalid username and password");
                } else {
                    showErrorToast("Something went wrong!!!");
                }
            }
        });
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputValue({
            ...inputValue,
            [name]: value.toString(),
        });
        setError({ ...Error, [name]: '' });
        
        // Clear email verification message when user starts typing
        if (showEmailVerificationMessage) {
            setShowEmailVerificationMessage(false);
        }
    };

    const onSubmit = () => {
        if (inputValue.email.toString().trim() === "") {
            setError({ ...Error, email: 'Please enter email' });
            return;
        }

        const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
        if (!emailRegex.test(inputValue.email)) {
            setError({ ...Error, email: 'Invalid Email' });
            return;
        }

        if (inputValue.password.toString().trim() === "") {
            setError({ ...Error, password: 'Please enter password' });
            return;
        }

        setIsLoading(true);
        logInUser(inputValue);
    }

    const handleResendVerification = async () => {
        if (!inputValue.email.trim()) {
            showErrorToast("Please enter your email address first");
            return;
        }

        setIsResendingVerification(true);
        try {
            const response = await resendEmailVerification(inputValue.email);

            if (response.status === 200 && response.data.success) {
                showSuccessToast("Verification email sent successfully! Check your inbox.");
                setShowEmailVerificationMessage(false);
            } else {
                showErrorToast(response.data.message || "Failed to send verification email");
            }
        } catch (error) {
            console.error('Error resending verification:', error);
            showErrorToast(error.response?.data?.message || "Failed to send verification email");
        } finally {
            setIsResendingVerification(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary to-secondary relative overflow-hidden">
            {/* Enhanced Animated Background Elements */}
            <div className="absolute inset-0">
                {/* Additional Smaller Orbs */}
                <div className="absolute top-1/4 left-1/3 w-32 h-32 bg-primary/50 rounded-full mix-blend-multiply filter blur-lg opacity-60 animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/3 w-24 h-24 bg-secondary/50 rounded-full mix-blend-multiply filter blur-lg opacity-60 animate-pulse animation-delay-1000"></div>
                <div className="absolute top-3/4 left-1/4 w-20 h-20 bg-green-400/50 rounded-full mix-blend-multiply filter blur-lg opacity-60 animate-pulse animation-delay-3000"></div>

                {/* Floating Particles */}
                <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-white/30 rounded-full animate-bounce"></div>
                <div className="absolute top-1/3 left-1/5 w-1 h-1 bg-white/40 rounded-full animate-bounce animation-delay-500"></div>
                <div className="absolute bottom-1/3 right-1/5 w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce animation-delay-1000"></div>
                <div className="absolute top-2/3 left-2/3 w-1 h-1 bg-white/50 rounded-full animate-bounce animation-delay-1500"></div>
                <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-white/40 rounded-full animate-bounce animation-delay-2000"></div>

                {/* Wave Effects */}
                <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-primary/20 to-transparent animate-wave"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-secondary/10 to-transparent animate-wave animation-delay-2000"></div>

                {/* Rotating Rings */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white/10 rounded-full animate-spin-slow"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/5 rounded-full animate-spin-slow animation-delay-1000" style={{ animationDirection: 'reverse' }}></div>

                {/* Grid Pattern with Animation */}
                <div className="absolute inset-0 opacity-20 animate-pulse" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: '60px 60px'
                }}></div>

                {/* Glowing Spots */}
                <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-primary/30 rounded-full animate-ping"></div>
                <div className="absolute bottom-1/4 right-1/4 w-3 h-3 bg-secondary/30 rounded-full animate-ping animation-delay-1000"></div>
                <div className="absolute top-3/4 right-1/3 w-2 h-2 bg-green-400/30 rounded-full animate-ping animation-delay-2000"></div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 flex min-h-screen">
                {/* Left Side - Branding */}
                <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary/20 to-secondary/20 backdrop-blur-sm items-center justify-center p-12">
                    <div className="text-center text-white">
                        <div className="mb-8">
                            <div className="w-32 h-32 bg-gradient-to-br from-white/20 to-white/10 rounded-full mx-auto mb-6 flex items-center justify-center backdrop-blur-sm border border-white/20 animate-pulse">
                                <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent animate-fade-in">
                                Catering Pro
                            </h1>
                            <p className="text-xl text-green-200 mb-8 animate-fade-in animation-delay-500">
                                Professional Catering Management System
                            </p>
                        </div>

                        {/* Feature Highlights */}
                        <div className="space-y-4 text-left max-w-md mx-auto">
                            <div className="flex items-center space-x-3 animate-slide-in-left animation-delay-700">
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <span className="text-green-200">Order Management</span>
                            </div>
                            <div className="flex items-center space-x-3 animate-slide-in-left animation-delay-900">
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce animation-delay-200">
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <span className="text-green-200">Menu Planning</span>
                            </div>
                            <div className="flex items-center space-x-3 animate-slide-in-left animation-delay-1100">
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce animation-delay-400">
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <span className="text-green-200">Inventory Tracking</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Login Form */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                    <div className="w-full max-w-md">
                        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-2xl animate-fade-in-up">
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-bold text-white mb-2 animate-fade-in">Welcome Back</h2>
                                <p className="text-gray-300 animate-fade-in animation-delay-300">Sign in to your account</p>
                            </div>

                            <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
                                <div className="animate-fade-in-up animation-delay-500">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Username
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                        <InputFields
                                            name="email"
                                            label="Username"
                                            type="text"
                                            placeholder="Enter your username"
                                            isHideLabel="true"
                                            disabled={isLoading}
                                            handleChange={(e) => handleChange(e)}
                                            value={inputValue?.email}
                                            className="pl-10 w-full h-12 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                                        />
                                    </div>
                                    {Error?.email && (
                                        <p className="mt-2 text-sm text-red-400 flex items-center">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {Error?.email}
                                        </p>
                                    )}
                                </div>

                                <div className="animate-fade-in-up animation-delay-700">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <InputFields
                                            name="password"
                                            label="Password"
                                            type="password"
                                            placeholder="Enter your password"
                                            isHideLabel="true"
                                            disabled={isLoading}
                                            handleChange={(e) => handleChange(e)}
                                            value={inputValue?.password}
                                            className="pl-10 w-full h-12 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                                        />
                                    </div>
                                    {Error?.password && (
                                        <p className="mt-2 text-sm text-red-400 flex items-center">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {Error?.password}
                                        </p>
                                    )}
                                </div>

                                {/* Email Verification Message */}
                                {showEmailVerificationMessage && (
                                    <div className="animate-fade-in-up animation-delay-800">
                                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0">
                                                    <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <h3 className="text-sm font-medium text-yellow-300">
                                                        Email Verification Required
                                                    </h3>
                                                    <div className="mt-2 text-sm text-yellow-200">
                                                        <p>Please verify your email address before logging in.</p>
                                                        <p className="mt-1">Check your inbox for a verification link or click below to resend.</p>
                                                    </div>
                                                    <div className="mt-3 flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleResendVerification}
                                                            disabled={isResendingVerification}
                                                            className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                        >
                                                            {isResendingVerification ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b border-yellow-200"></div>
                                                                    Sending...
                                                                </>
                                                            ) : (
                                                                'Resend Verification'
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowEmailVerificationMessage(false)}
                                                            className="bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded text-xs font-medium transition-colors duration-200"
                                                        >
                                                            Dismiss
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center justify-between animate-fade-in-up animation-delay-900">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-primary bg-white/10 border-white/20 rounded focus:ring-primary focus:ring-2"
                                        />
                                        <span className="ml-2 text-sm text-gray-300">Remember me</span>
                                    </label>
                                    <button
                                        type="button"
                                        className="text-sm text-white hover:text-green-300 transition-colors duration-300 font-medium  decoration-green-400/50 hover:decoration-green-300"
                                    >
                                        Forgot password?
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full h-12 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-xl hover:from-secondary hover:to-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center animate-fade-in-up animation-delay-1100"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                            </svg>
                                            {t('login')}
                                        </>
                                    )}
                                </button>

                                <div className="mt-4 text-center animate-fade-in-up animation-delay-1200">
                                    <span className="text-gray-300 mr-2">Don't have an account?</span>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/register')}
                                        className="text-white hover:text-green-300 transition-colors duration-300 font-medium underline decoration-green-400/50 hover:decoration-green-300"
                                    >
                                        Register now
                                    </button>
                                </div>
                            </form>

                            {/* Footer */}
                            <div className="mt-8 pt-6 border-t border-white/10 text-center animate-fade-in animation-delay-1300">
                                {/* <p className="text-xs text-gray-400">
                                    Powered by STRANGECODERS SOLUTIONS PVT. LTD
                                </p> */}
                                <p className="text-xs text-gray-400 mt-1">
                                    Catering Management System v1.0.0
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogIn;
